import React from 'react';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { pricingPlans, faqs } from '../mock';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

const Pricing = () => {

  useSEO({
    title: "Prijzen horeca website & online bestellen | Upxero",
    description:
      "Bekijk de transparante prijzen van Upxero voor horeca websites, online bestellen en WhatsApp automatisatie. Commissievrij, maandelijks opzegbaar in België & Nederland.",
    canonical: "https://www.upxero.com/pricing",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Prijzen Upxero",
      "url": "https://www.upxero.com/pricing",
      "description":
        "Overzicht van prijzen voor horeca websites, online bestellen en WhatsApp automatisatie zonder commissies.",
      "publisher": {
        "@type": "Organization",
        "name": "Upxero",
        "url": "https://www.upxero.com"
      },
      "mainEntity": {
        "@type": "OfferCatalog",
        "name": "Upxero Horeca Pakketten",
        "itemListElement": [
          {
            "@type": "Offer",
            "name": "Start",
            "price": "19",
            "priceCurrency": "EUR",
            "description":
              "Online bestellen voor restaurants met bestaande website, inclusief hosting en support.",
            "url": "https://www.upxero.com/pricing"
          },
          {
            "@type": "Offer",
            "name": "Grow",
            "price": "29",
            "priceCurrency": "EUR",
            "description":
              "Complete horeca website met online bestellen en WhatsApp assistent.",
            "url": "https://www.upxero.com/pricing"
          },
          {
            "@type": "Offer",
            "name": "Boost",
            "price": "49",
            "priceCurrency": "EUR",
            "description":
              "Alles-in-één groeipakket met optimalisatie, Google integratie en rapportage.",
            "url": "https://www.upxero.com/pricing"
          }
        ]
      }
    }
  });

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const comparisonFeatures = [
    {
      category: "Basis online bestellen",
      features: [
        {
          name: "Online bestelsysteem voor afhaling & levering",
          start: true,
          grow: true,
          boost: true,
        },
        {
          name: "Menu setup & volledige integratie",
          start: true,
          grow: true,
          boost: true,
        },
        {
          name: "Koppeling met GloriaFood (commissievrij)",
          start: true,
          grow: true,
          boost: true,
        },
        {
          name: "Online betalingen via Stripe mogelijk (+€25/maand) inclusief Bancontact, iDEAL en kredietkaart",
          start: true,
          grow: true,
          boost: true,
        },
        {
          name: "Hosting, updates & beveiliging volledig beheerd",
          start: true,
          grow: true,
          boost: true,
        },
      ],
    },
    {
      category: "Website & uitstraling",
      features: [
        {
          name: "Gebruik van bestaande website",
          start: true,
          grow: false,
          boost: false,
        },
        {
          name: "Professionele horeca website (mobile-first)",
          start: false,
          grow: true,
          boost: true,
        },
        {
          name: "SEO-basis voor lokale vindbaarheid",
          start: false,
          grow: true,
          boost: true,
        },
        {
          name: "Domeinnaam inbegrepen (.be / .nl / .com)",
          start: false,
          grow: true,
          boost: true,
        },
      ],
    },
    {
      category: "WhatsApp & klantenservice",
      features: [
        {
          name: "WhatsApp-knop op de website",
          start: true,
          grow: true,
          boost: true,
        },
        {
          name: "WhatsApp bestel-flow met automatische antwoorden",
          start: true,
          grow: true,
          boost: true,
        },
        {
          name: "Automatische doorverwijzing naar online bestellen",
          start: false,
          grow: true,
          boost: true,
        },
      ],
    },
    {
      category: "AI Assistant (alleen Boost)",
      features: [
        {
          name: "AI Bestel- & Klantenassistent (24/7)",
          start: false,
          grow: false,
          boost: true,
        },
        {
          name: "AI beantwoordt vrije vragen over menu, allergenen en openingsuren",
          start: false,
          grow: false,
          boost: true,
        },
        {
          name: "AI begeleidt klanten richting online bestelling",
          start: false,
          grow: false,
          boost: true,
        },
      ],
    },
    {
      category: "Optimalisatie & groei",
      features: [
        {
          name: "Conversie-optimalisatie",
          start: false,
          grow: false,
          boost: true,
        },
        {
          name: "Google Business integratie",
          start: false,
          grow: false,
          boost: true,
        },
        {
          name: "Rapportage & continue optimalisatie",
          start: false,
          grow: false,
          boost: true,
        },
      ],
    },
    {
      category: "Vacatures via Talent by Upxero",
      features: [
        {
          name: "Publicatie op talent.upxero.com",
          start: true,
          grow: true,
          boost: true,
        },
        {
          name: "Aantal actieve vacatures tegelijk",
          start: "1 vacature",
          grow: "3 vacatures",
          boost: "5 vacatures",
        },
        {
          name: "Featured vacature badge",
          start: false,
          grow: true,
          boost: true,
        },
        {
          name: "Voorrang in zoekresultaten",
          start: false,
          grow: true,
          boost: true,
        },
        {
          name: "Prioritaire vacatureplaatsing",
          start: false,
          grow: false,
          boost: true,
        },
        {
          name: "Extra promotionele ondersteuning",
          start: false,
          grow: false,
          boost: true,
        },
      ],
    },
    {
      category: "Support & ontzorging",
      features: [
        {
          name: "Email & WhatsApp support",
          start: true,
          grow: true,
          boost: true,
        },
        {
          name: "Prioriteit support & persoonlijk advies",
          start: false,
          grow: false,
          boost: true,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section 
      id="plans"
      className="pt-32 pb-20 bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Transparante{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-blue-700">
                Prijzen
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Kies het plan dat bij uw restaurant past. Geen verborgen kosten, 
              geen commissies op bestellingen. Maandelijks opzegbaar.
            </p>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-6 py-3 rounded-full font-semibold">
              <Check size={20} />
              Alle plannen zijn commissievrij
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative bg-white transition-all hover:shadow-2xl ${
                  plan.popular
                    ? 'border-2 border-orange-500 shadow-xl scale-105'
                    : 'border border-gray-200 hover:scale-105'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-orange-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MEEST POPULAIR
                    </div>
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>

                    <div className="mb-2">
                      <span className="text-5xl font-bold text-blue-900">
                        €{plan.price}
                      </span>
                      <span className="text-gray-600 text-lg">
                        /{plan.period}
                      </span>
                    </div>

                    {/* SETUP ACTIE */}
                    {plan.setupPrice && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-red-600">
                          Tijdelijke actie: 50% korting op de setup
                        </p>
                        <p className="text-sm text-gray-500">
                          <span className="line-through mr-1">
                            €{plan.setupPrice}
                          </span>
                          <span className="text-green-700 font-semibold">
                            €{plan.setupPrice / 2} setup
                          </span>
                        </p>
                      </div>
                    )}

                    <p className="text-gray-600">
                      {plan.description}
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/contact">
                    <Button
                      className={`w-full py-6 rounded-lg font-semibold text-lg transition-all ${
                        plan.popular
                          ? 'bg-orange-500 hover:bg-orange-600 text-white hover:shadow-xl'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}
                    >
                      {plan.popular ? 'Start Nu' : 'Kies Dit Plan'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-2xl p-10 text-center shadow-xl">
            <h3 className="text-3xl font-bold mb-4">
              Op zoek naar personeel?
            </h3>
            <p className="text-lg mb-6 text-blue-100">
              Publiceer uw vacatures via ons horeca jobplatform en bereik actief werkzoekenden.
            </p>

            <a
              href="https://talent.upxero.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-orange-500 hover:bg-orange-600 px-8 py-4 rounded-xl font-semibold text-lg transition shadow-lg"
            >
              Bezoek talent.upxero.com
            </a>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Niet zeker welk plan bij u past? Neem contact met ons op voor persoonlijk advies.
            </p>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg rounded-lg font-semibold">
                Ontvang Voorstel Op Maat
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* No Commission Section */}
      <section
        id="no-commissions"
        className="py-20 bg-gradient-to-br from-blue-50 via-white to-blue-100"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Waarom wij{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-blue-600">
                geen commissies
              </span>{' '}
              aanrekenen
            </h2>
            <p className="text-xl text-gray-600">
              Uw bestellingen zijn van u. Niet van een platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center">
            
            {/* Left: explanation */}
            <div className="space-y-6 text-gray-700 text-lg">
              <p>
                Grote bestelplatformen rekenen vaak{' '}
                <strong>15% tot 30% commissie per bestelling</strong>.  
                Dat lijkt misschien handig, maar op lange termijn kost het u
                duizenden euro’s per jaar.
              </p>

              <p>
                Bij <strong>Upxero</strong> werken we anders.  
                U betaalt een <strong>vast maandbedrag</strong>, ongeacht hoeveel
                bestellingen u ontvangt.
              </p>

              <p>
                Zo houdt u:
              </p>

              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="text-blue-900 mt-1" size={22} />
                  <span>
                    <strong>100% van uw omzet</strong> — geen afroming per bestelling
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-blue-900 mt-1" size={22} />
                  <span>
                    <strong>Direct contact met uw klanten</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-blue-900 mt-1" size={22} />
                  <span>
                    <strong>Volledige controle</strong> over uw menu, prijzen en acties
                  </span>
                </li>
              </ul>
            </div>

            {/* Right: visual card */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Concreet verschil
              </h3>

              <div className="space-y-4">
                <div className="flex justify-between items-center bg-red-50 p-4 rounded-lg">
                  <span className="font-semibold text-gray-700">
                    Platformen met commissie
                  </span>
                  <span className="text-red-600 font-bold">
                    −15% tot −30% per bestelling
                  </span>
                </div>

                <div className="flex justify-between items-center bg-blue-50 p-4 rounded-lg">
                  <span className="font-semibold text-gray-700">
                    Upxero
                  </span>
                  <span className="text-blue-900 font-bold">
                    € vast bedrag / maand
                  </span>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Link to="/contact">
                  <Button className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-5 text-lg rounded-lg font-semibold transition-all hover:shadow-lg">
                    Vraag gratis preview aan
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Alles inbegrepen, duidelijk vergeleken
            </h2>
            <p className="text-xl text-gray-600">
              Geen technische details, wel volledige duidelijkheid over wat wij voor u regelen.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                      Functie
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                      Start
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900 bg-orange-50">
                      Grow
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                      Boost
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((category, categoryIndex) => (
                    <React.Fragment key={categoryIndex}>
                      <tr className="bg-blue-50">
                        <td
                          colSpan={4}
                          className="px-6 py-3 text-left font-bold text-blue-900 text-sm uppercase tracking-wide"
                        >
                          {category.category}
                        </td>
                      </tr>
                      {category.features.map((feature, featureIndex) => (
                        <tr
                          key={featureIndex}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 text-gray-700">{feature.name}</td>
                          <td className="px-6 py-4 text-center">
                            {feature.start ? (
                              <Check className="text-green-600 mx-auto" size={20} />
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center bg-orange-50/30">
                            {feature.grow ? (
                              <Check className="text-green-600 mx-auto" size={20} />
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            {feature.boost ? (
                              <Check className="text-green-600 mx-auto" size={20} />
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <HelpCircle className="text-blue-900" size={32} />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Veelgestelde Vragen
            </h2>
            <p className="text-xl text-gray-600">
              Antwoorden op de meest gestelde vragen over prijzen en diensten
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                className="bg-gray-50 rounded-lg px-6 border-0"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-900 py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center p-8 bg-gradient-to-br from-blue-50 to-orange-50 rounded-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Nog vragen?
            </h3>
            <p className="text-gray-600 mb-6">
              Neem contact met ons op en we helpen u graag verder
            </p>
            <Link to="/contact">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-lg font-semibold transition-all hover:shadow-xl inline-flex items-center gap-2">
                Contacteer Ons
                <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
